import React, { useState, useEffect } from 'react';
import { 
  X, CheckCircle2, Smartphone, Mail, Loader2, Plus, 
  Save, RotateCcw, Maximize2, Minimize2, ZoomIn, ZoomOut, Eye, Send, ArrowRight, ArrowLeft
} from 'lucide-react';
import type { Demand } from '../types';
import { demandsAPI, crmAPI } from '../api';

interface ApprovalModalProps {
  demand: Demand;
  onClose: () => void;
  onConfirm: (data: {
    sendWhatsApp: boolean;
    sendEmail: boolean;
    messageText: string;
    whatsappNumbers?: string[];
    emailAddresses?: string[];
    pdfBase64?: string;
    templateCode?: string;
    variables?: any;
    authorityId?: string;
    editedHtml?: string;
    templateId?: string;
  }) => Promise<void>;
}

export const ApprovalModal: React.FC<ApprovalModalProps> = ({
  demand,
  onClose,
  onConfirm,
}) => {
  const [step, setStep] = useState<number>(1); // 1: Config & Vars, 2: Editor Inline, 3: PDF Preview
  const [sendWhatsApp, setSendWhatsApp] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);
  const initialPhone = demand.contact?.phone || demand.senderPhone;
  const initialEmail = demand.contact?.email;
  
  const [whatsappNumbers, setWhatsappNumbers] = useState<string[]>(initialPhone ? [initialPhone] : []);
  const [emailAddresses, setEmailAddresses] = useState<string[]>(initialEmail ? [initialEmail] : []);
  const [messageText, setMessageText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [whatsappInput, setWhatsappInput] = useState('');
  const [emailInput, setEmailInput] = useState('');
  
  // Estados dos Templates e Autoridades
  const [templates, setTemplates] = useState<any[]>([]);
  const [authorities, setAuthorities] = useState<any[]>([]);
  const [selectedTemplateCode, setSelectedTemplateCode] = useState<string>('');
  const [selectedAuthorityId, setSelectedAuthorityId] = useState<string>('');
  const [dynamicVars, setDynamicVars] = useState<Record<string, string>>({});
  const [resourceObjects, setResourceObjects] = useState<any[]>([]);
  const [resolvedMunicipality, setResolvedMunicipality] = useState<any>(null);
  const [resolvedEntity, setResolvedEntity] = useState<any>(null);
  const [resolvedPerson, setResolvedPerson] = useState<any>(null);
  const [crmLoadedContacts, setCrmLoadedContacts] = useState(false);

  // Editor Inline & A4 states
  const [editedHtml, setEditedHtml] = useState<string>('');
  const [customVereador, setCustomVereador] = useState<string>('');
  const [customObjeto, setCustomObjeto] = useState<string>('');
  const [zoom, setZoom] = useState<number>(100);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [autoSaveActive, setAutoSaveActive] = useState<boolean>(true);
  const [lastSaved, setLastSaved] = useState<string>('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);

  useEffect(() => {
    demandsAPI.getTemplates().then(setTemplates).catch(console.error);
    demandsAPI.getAuthorities().then(setAuthorities).catch(console.error);
    crmAPI.listResourceObjects().then(setResourceObjects).catch(console.error);
  }, []);

  // Buscar vereador sugerido e objeto padrão
  useEffect(() => {
    if (demand) {
      // Vereador
      const sugeridoVereador = (resolvedPerson?.role?.toLowerCase().includes('vereador') ? resolvedPerson.name : null) || 
                       demand.summary?.match(/(?:vereador|solicitação do|solicitação de)\s+([^,\n\.\(\)]+)/i)?.[1]?.trim() || 
                       'Ricardo Rio';
      setCustomVereador(sugeridoVereador);

      // Objeto
      const area = (demand.themeArea || '').toLowerCase().replace(/[_\s-]/g, '');
      const tituloLower = (demand.title || '').toLowerCase();
      let sugeridoObjeto = demand.title || 'Investimentos Municipais';
      
      if (area.includes('saude') || area.includes('saúde') || tituloLower.includes('saúde') || tituloLower.includes('custeio da saude') || tituloLower.includes('ubs')) {
        sugeridoObjeto = 'Custeio da Saúde';
      } else if (area.includes('infra') || area.includes('obras') || area.includes('vicinal') || area.includes('paviment') || tituloLower.includes('vicina') || tituloLower.includes('repar') || tituloLower.includes('manuten')) {
        sugeridoObjeto = 'Reparos e Manutenção de Vicinais';
      } else if (area.includes('educa') || tituloLower.includes('escola') || tituloLower.includes('creche')) {
        sugeridoObjeto = 'Investimentos na Educação';
      } else if (area.includes('social') || area.includes('cras') || area.includes('apae')) {
        sugeridoObjeto = 'Assistência Social';
      } else if (area.includes('esport') || tituloLower.includes('quadra') || tituloLower.includes('esportivo')) {
        sugeridoObjeto = 'Infraestrutura Esportiva';
      } else if (area.includes('agricultura') || area.includes('rural') || tituloLower.includes('trator')) {
        sugeridoObjeto = 'Desenvolvimento Agrícola e Rural';
      }
      setCustomObjeto(sugeridoObjeto);
    }
  }, [demand, resolvedPerson, resolvedMunicipality, resolvedEntity]);

  useEffect(() => {
    if (demand.crmSuggestionStatus === 'CONFIRMED' || demand.crmSuggestionConfidence === 'HIGH') {
      if (demand.crmMunicipalityId) {
        crmAPI.listMunicipalities()
          .then(list => {
            const found = list.find(m => m.id === demand.crmMunicipalityId);
            if (found) setResolvedMunicipality(found);
          })
          .catch(console.error);
      }
      if (demand.crmEntityId) {
        crmAPI.listEntities()
          .then(list => {
            const found = list.find(e => e.id === demand.crmEntityId);
            if (found) setResolvedEntity(found);
          })
          .catch(console.error);
      }
      if (demand.crmPersonId) {
        crmAPI.listPeople()
          .then(list => {
            const found = list.find(p => p.id === demand.crmPersonId);
            if (found) setResolvedPerson(found);
          })
          .catch(console.error);
      }
    }
  }, [demand]);

  useEffect(() => {
    if (!crmLoadedContacts) {
      const phones: string[] = [...whatsappNumbers];
      const emails: string[] = [...emailAddresses];
      let changed = false;

      if (resolvedPerson) {
        if (resolvedPerson.phone && !phones.includes(resolvedPerson.phone)) {
          phones.push(resolvedPerson.phone);
          changed = true;
        }
        if (resolvedPerson.email && !emails.includes(resolvedPerson.email)) {
          emails.push(resolvedPerson.email);
          changed = true;
        }
      }
      if (resolvedEntity) {
        if (resolvedEntity.phone && !phones.includes(resolvedEntity.phone)) {
          phones.push(resolvedEntity.phone);
          changed = true;
        }
        if (resolvedEntity.email && !emails.includes(resolvedEntity.email)) {
          emails.push(resolvedEntity.email);
          changed = true;
        }
      }

      if (changed) {
        setWhatsappNumbers(phones);
        setEmailAddresses(emails);
        setCrmLoadedContacts(true);
      }
    }
  }, [resolvedPerson, resolvedEntity, crmLoadedContacts]);

  const handleAddWhatsapp = (phone?: string) => {
    const val = phone || whatsappInput;
    if (val.trim() && !whatsappNumbers.includes(val.trim())) {
      setWhatsappNumbers([...whatsappNumbers, val.trim()]);
    }
    setWhatsappInput('');
  };

  const handleRemoveWhatsapp = (phone: string) => {
    setWhatsappNumbers(whatsappNumbers.filter(p => p !== phone));
  };

  const handleAddEmail = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const val = emailInput.trim();
      if (val && !emailAddresses.includes(val)) {
        setEmailAddresses([...emailAddresses, val]);
      }
      setEmailInput('');
    }
  };

  const handleRemoveEmail = (email: string) => {
    setEmailAddresses(emailAddresses.filter(e => e !== email));
  };

  const activeTemplate = templates.find(t => t.code === selectedTemplateCode);
  const activeAuthority = authorities.find(a => a.id === selectedAuthorityId);

  // Inicializa variáveis dinâmicas e pré-preenche com dados da demanda
  useEffect(() => {
    if (activeTemplate && Array.isArray(activeTemplate.variables)) {
      const today = new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
      const protocolo = demand.protocol.replace('DEM-', '');
      const deputadoName = demand.cabinet === 'STATE'
        ? 'Dani Alonso'
        : demand.cabinet === 'FEDERAL' ? 'Capitão Augusto' : '';
      const fmtValor = (v: any) => {
        if (!v) return '';
        const num = Number(String(v).replace(/[^\d.,]/g, '').replace(',', '.'));
        if (isNaN(num)) return String(v);
        return `R$ ${num.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
      };
      const valorAprovado = fmtValor(demand.valueApproved);
      const valorSolicitado = fmtValor(demand.valueRequested);
      const valorPrincipal = valorAprovado || valorSolicitado;

      const vars: Record<string, string> = {};
      activeTemplate.variables.forEach((v: any) => {
        const key: string = v.key;
        const k = key.toLowerCase().replace(/[_\s-]/g, '');

        // Mapear por padrões de nome
        if (k.includes('cidade') || k === 'municipio' || k === 'nomemunicipio' || k === 'nomedo municipio')
          vars[key] = demand.city || '';
        else if (k === 'uf' || k === 'ufmunicipio' || k === 'estado')
          vars[key] = demand.uf || 'SP';
        else if (k.includes('deputado') || k === 'parlamentarnome' || k === 'nomeparlamentar')
          vars[key] = deputadoName;
        else if ((k.includes('data') && k.includes('oficio')) || k === 'data' || k === 'dataoficio')
          vars[key] = today;
        else if ((k.includes('numero') || k.includes('número')) && (k.includes('oficio') || k.includes('protocolo')))
          vars[key] = protocolo;
        else if (k === 'numerooficio' || k === 'numerodooficio')
          vars[key] = protocolo;
        else if (k.includes('codigo') || k.includes('transferencia') || k.includes('transparencia'))
          vars[key] = demand.protocol || '';
        else if ((k.includes('valor') && (k.includes('destinado') || k.includes('aprovado'))))
          vars[key] = valorAprovado || valorSolicitado;
        else if (k.includes('valor') && (k.includes('solicitado') || k.includes('pedido')))
          vars[key] = valorSolicitado;
        else if (k === 'valor' || k === 'valoroficio')
          vars[key] = valorPrincipal;
        else if (k.includes('assunto') || k.includes('titulo') || k === 'subject')
          vars[key] = demand.title || '';
        else if (k.includes('bemsolicitado') || k.includes('bem_solicitado') || k === 'bem' || k === 'objeto' || k.includes('bemmaterial'))
          vars[key] = demand.title || '';
        else if (k.includes('prefeito') || k === 'nomeprefeito')
          vars[key] = (resolvedPerson?.role?.toLowerCase().includes('prefeito') ? resolvedPerson.name : null) || resolvedMunicipality?.notes?.match(/Prefeito:\s*([^\n]+)/)?.[1] || '';
        else if (k.includes('vereador') || k === 'nomevereador')
          vars[key] = (resolvedPerson?.role?.toLowerCase().includes('vereador') ? resolvedPerson.name : null) || '';
        else if (k.includes('telefone') || k === 'fone' || k === 'tel')
          vars[key] = resolvedPerson?.phone || resolvedEntity?.phone || resolvedMunicipality?.phone || '';
        else if (k.includes('email') || k === 'emailcontato')
          vars[key] = resolvedPerson?.email || resolvedEntity?.email || resolvedMunicipality?.email || '';
        else if (k.includes('endereco') || k.includes('endereço') || k === 'address')
          vars[key] = resolvedEntity?.address || resolvedMunicipality?.addressPref || '';
        else if (k.includes('cnpj') || k === 'cnpjentidade')
          vars[key] = resolvedEntity?.cnpj || '';
        else
          vars[key] = '';
      });
      setDynamicVars(vars);
    } else {
      setDynamicVars({});
    }
  }, [selectedTemplateCode, activeTemplate, demand, resolvedMunicipality, resolvedEntity, resolvedPerson]);

  // Gera o HTML do template padrão (sem template oficial) com dados da demanda seguindo o layout oficial do PDF em anexo
  const buildDefaultHtml = () => {
    const today = new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
    const protocolo = demand.protocol.replace('DEM-', '');
    const cidade = demand.city || '_____________';
    const uf = demand.uf || 'SP';
    const solicitante = demand.solicitanteName || '_____________';
    const vereador = customVereador || 'Ricardo Rio';
    const objetoDestinado = customObjeto || 'Investimentos Municipais';

    // Se houver valor aprovado ou solicitado real no sistema, formata-o, senão retorna nulo para omitir a expressão de valor
    const valorStr = (demand.valueApproved && Number(demand.valueApproved) > 0)
      ? ` no valor de <strong>R$ ${Number(demand.valueApproved).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong>,`
      : (demand.valueRequested && Number(demand.valueRequested) > 0)
        ? ` no valor de <strong>R$ ${Number(demand.valueRequested).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong>,`
        : '';

    const p = (txt: string) => `<p style="margin:0 0 16px 0;text-align:justify;line-height:1.6;font-family:Arial, sans-serif;font-size:15px;color:#000;">${txt}</p>`;

    return [
      `<div style="display:flex;justify-content:space-between;align-items:center;width:100%;margin-bottom:30px;font-family:Arial, sans-serif;font-size:15px;">`,
      `  <div style="text-align:left;font-weight:bold;">Ofício nº ${protocolo}/2026</div>`,
      `  <div style="text-align:right;">São Paulo, ${today}.</div>`,
      `</div>`,
      `<div style="margin-bottom:35px;font-family:Arial, sans-serif;font-size:15px;text-align:left;color:#000;">`,
      `  <div style="font-weight:bold;margin-bottom:2px;">Exmo. Sr.</div>`,
      `  <div style="font-weight:bold;margin-bottom:2px;">${solicitante}</div>`,
      `  <div style="font-weight:bold;">DD. Prefeito de ${cidade}/${uf}</div>`,
      `</div>`,
      p(`Prezado Senhor,`),
      p(`Cumprimentando-o cordialmente, informamos que foi realizado o pagamento ao município de <strong>${cidade}</strong> de <strong>Emenda Impositiva (${demand.protocol.replace('DEM-', '')})</strong>, indicada pelos <strong>Deputados Dani Alonso e Capitão Augusto</strong>, atendendo à solicitação do vereador ${vereador},${valorStr} para <strong>${objetoDestinado}</strong>.`),
      p(`Com satisfação, comunicamos que o recurso já foi pago e encontra-se à disposição do município para utilização conforme o objetivo proposto.`),
      p(`Para quaisquer informações adicionais ou esclarecimentos, nossa Assessoria Parlamentar encontra-se à disposição na Câmara dos Deputados, pelo telefone (61) 3215-5883.`),
      p(`Reiteramos nosso compromisso com o desenvolvimento de <strong>${cidade}</strong> e com o fortalecimento dos serviços públicos que beneficiam diretamente a população.`),
      p(`Atenciosamente,`),
    ].join('');
  };

  // Atualiza o texto do ofício timbrado de acordo com o template e variáveis
  useEffect(() => {
    const today = new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });

    if (activeTemplate && activeTemplate.htmlContent) {
      // Template oficial com HTML — processar variáveis
      let tempHtml = activeTemplate.htmlContent;
      const normalizedVars: Record<string, string> = {};
      Object.entries(dynamicVars).forEach(([key, val]) => {
        const normKey = key.toLowerCase().replace(/[_\s-]/g, '');
        normalizedVars[normKey] = val;
      });

      tempHtml = tempHtml.replace(/\{\{\s*([a-zA-Z0-9_À-ÿ\s\-\_]+)\s*\}\}/g, (match: string, varName: string) => {
        const normName = varName.toLowerCase().replace(/[_\s-]/g, '');
        if (normalizedVars[normName] !== undefined) {
          return normalizedVars[normName] || `[${varName.toUpperCase()}]`;
        }
        return match;
      });

      tempHtml = tempHtml.replace(/\[\s*([a-zA-Z0-9_À-ÿ\s\-\_]+)\s*\]/g, (match: string, varName: string) => {
        const normName = varName.toLowerCase().replace(/[_\s-]/g, '');
        if (normalizedVars[normName] !== undefined) {
          return normalizedVars[normName] || `[${varName.toUpperCase()}]`;
        }
        return match;
      });

      setEditedHtml(tempHtml);
    } else if (activeTemplate && selectedTemplateCode === 'ALESP_BEM_MATERIAL') {
      const cidade = dynamicVars['cidade'] || demand.city || '';
      const deputado = dynamicVars['deputado'] || 'Dani Alonso';
      const valor = dynamicVars['valor'] || (demand.valueRequested ? `R$ ${demand.valueRequested}` : '');
      const bemSolicitado = dynamicVars['bem_solicitado'] || dynamicVars['bemsolicitado'] || demand.title || 'Bem Material';
      const p = (txt: string) => `<p style="margin:0 0 14px 0;text-align:justify;line-height:1.8;">${txt}</p>`;
      setEditedHtml([
        p(`<strong>Ofício Especial nº ${demand.protocol.replace('DEM-', '')}/2026-ALESP</strong>`),
        p(`São Paulo, ${today}.`),
        `<p style="margin:0 0 14px 0;"></p>`,
        p(`À Presidência da ALESP`),
        p(`<strong>Assunto:</strong> Indicação de Bem Material – Município de ${cidade}`),
        `<p style="margin:0 0 14px 0;"></p>`,
        p(`Senhor Presidente,`),
        `<p style="margin:0 0 14px 0;"></p>`,
        p(`Indico à Mesa Diretora da Assembleia Legislativa do Estado de São Paulo o repasse do bem material: <strong>${bemSolicitado}</strong> para o município de ${cidade}, conforme indicação formalizada por este gabinete sob a representação do(a) Deputado(a) ${deputado}.`),
        valor ? p(`Valor de referência estimado: <strong>${valor}</strong>.`) : '',
        p(`Nestes termos, solicito os encaminhamentos necessários.`),
        `<p style="margin:0 0 14px 0;"></p>`,
        p(`Atenciosamente,`),
        p(`<strong>${deputado}</strong><br/>Assembleia Legislativa de São Paulo`),
      ].join(''));
    } else {
      // Template padrão (GABINETE) — gerar HTML com dados da demanda
      setEditedHtml(buildDefaultHtml());
    }

    // Manter messageText simples para fallback no WhatsApp
    setMessageText(`Ofício ref. protocolo ${demand.protocol} — ${demand.title} — ${demand.city}-${demand.uf}. Aprovada em ${today}.`);
  }, [demand, selectedTemplateCode, selectedAuthorityId, dynamicVars, authorities, activeTemplate]);

  // Carrega rascunho salvo anteriormente se existir
  const handleNextStepToEditor = () => {
    const savedDraft = localStorage.getItem('draft_doc_demand_' + demand.id);
    if (savedDraft) {
      if (confirm('Encontramos um rascunho não finalizado para esta aprovação. Deseja recuperá-lo?')) {
        setEditedHtml(savedDraft);
        setStep(2);
        return;
      }
    }

    if (!editedHtml && activeTemplate && activeTemplate.htmlContent) {
      // Processar variáveis no HTML
      let tempHtml = activeTemplate.htmlContent;
      const normalizedVars: Record<string, string> = {};
      Object.entries(dynamicVars).forEach(([key, val]) => {
        const normKey = key.toLowerCase().replace(/[_\s-]/g, '');
        normalizedVars[normKey] = val;
      });

      tempHtml = tempHtml.replace(/\{\{\s*([a-zA-Z0-9_À-ÿ\s\-\_]+)\s*\}\}/g, (match: string, varName: string) => {
        const normName = varName.toLowerCase().replace(/[_\s-]/g, '');
        if (normalizedVars[normName] !== undefined) {
          return normalizedVars[normName] || `[${varName.toUpperCase()}]`;
        }
        return match;
      });

      tempHtml = tempHtml.replace(/\[\s*([a-zA-Z0-9_À-ÿ\s\-\_]+)\s*\]/g, (match: string, varName: string) => {
        const normName = varName.toLowerCase().replace(/[_\s-]/g, '');
        if (normalizedVars[normName] !== undefined) {
          return normalizedVars[normName] || `[${varName.toUpperCase()}]`;
        }
        return match;
      });

      setEditedHtml(tempHtml);
    } else if (!editedHtml) {
      // Template padrão — gerar HTML estruturado com dados da demanda
      setEditedHtml(buildDefaultHtml());
    }

    setStep(2);
  };

  // AutoSave Timer
  useEffect(() => {
    let intervalId: any;
    if (autoSaveActive && step === 2 && editedHtml) {
      intervalId = setInterval(() => {
        localStorage.setItem('draft_doc_demand_' + demand.id, editedHtml);
        setLastSaved(new Date().toLocaleTimeString('pt-BR'));
        setHasUnsavedChanges(false);
      }, 10000);
    }
    return () => clearInterval(intervalId);
  }, [autoSaveActive, step, editedHtml, demand.id]);

  const handleManualSave = () => {
    localStorage.setItem('draft_doc_demand_' + demand.id, editedHtml);
    setLastSaved(new Date().toLocaleTimeString('pt-BR'));
    setHasUnsavedChanges(false);
    alert('Rascunho salvo com sucesso no navegador!');
  };

  const handleRegenerar = () => {
    if (confirm('Tem certeza de que deseja descartar todas as edições manuais e reverter para o template original?')) {
      localStorage.removeItem('draft_doc_demand_' + demand.id);
      setEditedHtml('');
      setHasUnsavedChanges(false);
      // Forçar regeneração no próximo render
      if (activeTemplate && activeTemplate.htmlContent) {
        let tempHtml = activeTemplate.htmlContent;
        const normalizedVars: Record<string, string> = {};
        Object.entries(dynamicVars).forEach(([key, val]) => {
          const normKey = key.toLowerCase().replace(/[_\s-]/g, '');
          normalizedVars[normKey] = val;
        });

        tempHtml = tempHtml.replace(/\{\{\s*([a-zA-Z0-9_À-ÿ\s\-\_]+)\s*\}\}/g, (match: string, varName: string) => {
          const normName = varName.toLowerCase().replace(/[_\s-]/g, '');
          if (normalizedVars[normName] !== undefined) {
            return normalizedVars[normName] || `[${varName.toUpperCase()}]`;
          }
          return match;
        });

        tempHtml = tempHtml.replace(/\[\s*([a-zA-Z0-9_À-ÿ\s\-\_]+)\s*\]/g, (match: string, varName: string) => {
          const normName = varName.toLowerCase().replace(/[_\s-]/g, '');
          if (normalizedVars[normName] !== undefined) {
            return normalizedVars[normName] || `[${varName.toUpperCase()}]`;
          }
          return match;
        });

        setEditedHtml(tempHtml);
      } else {
        setEditedHtml(buildDefaultHtml());
      }
    }
  };

  const handleClose = () => {
    if (hasUnsavedChanges) {
      if (!confirm('Você tem alterações não salvas no editor de ofício. Tem certeza de que deseja sair?')) {
        return;
      }
    }
    onClose();
  };

  const getPdfElement = () => {
    const element = document.createElement('div');
    const origin = window.location.origin;
    
    element.innerHTML = `
      <div style="padding: 30px 40px; font-family: 'Times New Roman', Times, serif; color: #000; background: #fff; line-height: 1.6; max-width: 800px; margin: 0 auto;">
        
        <!-- Cabeçalho com Logos (Bloqueado) -->
        <div style="display: flex; justify-content: center; gap: 60px; align-items: center; margin-bottom: 40px; text-align: center;">
          <div style="flex: 1; display: flex; justify-content: center;">
            <img src="${origin}/logo-camara.png" style="max-height: 150px; max-width: 100%; object-fit: contain;" />
          </div>
          <div style="flex: 1; display: flex; justify-content: center;">
            <img src="${origin}/logo-alesp.png" style="max-height: 150px; max-width: 100%; object-fit: contain;" />
          </div>
        </div>

        <!-- Corpo do Ofício (Conteúdo Editado) -->
        <div style="font-size: 15px; text-align: justify; margin-bottom: 60px; min-height: 400px; line-height: 1.8;">
          ${editedHtml || messageText.replace(/\n/g, '<br/>')}
        </div>

        <!-- Assinaturas (Bloqueado) -->
        <div style="display: flex; justify-content: center; gap: 60px; align-items: flex-end; margin-top: 80px; text-align: center;">
          ${activeAuthority ? `
            <div style="flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center;">
              <img src="${activeAuthority.signatureImageUrl ? (activeAuthority.signatureImageUrl.startsWith('/') ? origin + activeAuthority.signatureImageUrl : activeAuthority.signatureImageUrl) : ''}" style="max-height: 150px; max-width: 100%; object-fit: contain;" />
            </div>
          ` : `
            <div style="flex: 1; display: flex; justify-content: center; flex-direction: column; align-items: center;">
              <img src="${origin}/assinatura-capitao.png" style="max-height: 130px; max-width: 100%; object-fit: contain;" />
            </div>
            <div style="flex: 1; display: flex; justify-content: center; flex-direction: column; align-items: center;">
              <img src="${origin}/assinatura-dani.png" style="max-height: 130px; max-width: 100%; object-fit: contain;" />
            </div>
          `}
        </div>
      </div>
    `;
    return element;
  };

  const generatePdfBlob = async (): Promise<{ blob: Blob; dataUri: string }> => {
    const element = getPdfElement();

    element.style.position = 'absolute';
    element.style.left = '-9999px';
    element.style.top = '0';
    element.style.width = '800px';
    document.body.appendChild(element);

    try {
      await Promise.allSettled(
        Array.from(element.querySelectorAll('img')).map(
          img => new Promise(res => { img.onload = res; img.onerror = res; })
        )
      );

      const html2canvas = (await import('html2canvas')).default;
      const { jsPDF } = await import('jspdf');

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        windowWidth: 800,
      });

      const imgData = canvas.toDataURL('image/jpeg', 0.98);
      const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });

      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const contentW = pageW - margin * 2;
      const imgH = (canvas.height * contentW) / canvas.width;

      let remainingH = imgH;
      let firstPage = true;
      while (remainingH > 0) {
        if (!firstPage) pdf.addPage();
        firstPage = false;
        const sliceH = Math.min(remainingH, pageH - margin * 2);
        pdf.addImage(imgData, 'JPEG', margin, margin, contentW, imgH, undefined, 'FAST', 0);
        remainingH -= sliceH;
      }

      const blob = pdf.output('blob');
      const dataUri = pdf.output('datauristring');
      return { blob, dataUri };
    } finally {
      document.body.removeChild(element);
    }
  };

  const handlePreviewPDF = async () => {
    setIsSubmitting(true);
    try {
      const { blob } = await generatePdfBlob();
      if (previewPdfUrl) URL.revokeObjectURL(previewPdfUrl);
      const url = URL.createObjectURL(blob);
      setPreviewPdfUrl(url);
      setStep(3); // Avança para a etapa de preview
    } catch (e) {
      console.error(e);
      alert('Erro ao gerar a visualização prévia do PDF.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      let pdfBase64: string | undefined;
      try {
        const { dataUri } = await generatePdfBlob();
        pdfBase64 = dataUri;
      } catch(pdfErr) {
        console.error('Erro ao gerar PDF na submissão:', pdfErr);
      }

      await onConfirm({
        sendWhatsApp,
        sendEmail,
        messageText,
        whatsappNumbers,
        emailAddresses,
        pdfBase64,
        templateCode: selectedTemplateCode || undefined,
        variables: selectedTemplateCode ? dynamicVars : undefined,
        authorityId: selectedAuthorityId || undefined,
        editedHtml: editedHtml || undefined,
        templateId: activeTemplate?.id || undefined
      });

      // Limpar rascunho do localStorage ao enviar com sucesso
      localStorage.removeItem('draft_doc_demand_' + demand.id);
      onClose();
    } catch (err: any) {
      alert(err.message || 'Erro ao processar aprovação.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center p-0 md:p-4 bg-slate-950/80 backdrop-blur-sm ${isFullscreen ? 'p-0' : ''}`}>
      <div className={`bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-2xl flex flex-col overflow-hidden transition-all duration-300 ${
        isFullscreen ? 'w-screen h-screen rounded-none' : 'rounded-3xl w-full max-w-5xl h-[95vh] md:h-[90vh]'
      }`}>
        
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-800/60 bg-slate-100 dark:bg-slate-950">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-800 dark:text-slate-100 font-sans">
                Aprovar Demanda e Gerar Ofício
              </h2>
              <div className="text-xs text-slate-400 font-mono mt-0.5">
                Protocolo: {demand.protocol} • Etapa {step} de 3
              </div>
            </div>
          </div>
          <button 
            onClick={handleClose}
            className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-all"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Step Wizard Body */}
        <div className="flex-1 overflow-y-auto p-6 bg-slate-50/50 dark:bg-slate-950/20">
          
          {/* STEP 1: Config & Variables */}
          {step === 1 && (
            <div className="space-y-6 max-w-3xl mx-auto">
              
              {/* Opções de canais de notificação */}
              <div className="space-y-3 bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200 dark:border-slate-800">
                <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200 font-sans">1. Canais de Envio e Notificação</h3>
                <p className="text-xs text-slate-500">O ofício e as notificações serão enviados nos canais marcados.</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                  {/* WhatsApp selection */}
                  <label className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${sendWhatsApp ? 'border-emerald-500 bg-emerald-500/5' : 'border-slate-200 dark:border-slate-800 hover:border-emerald-500/20 bg-slate-950/20'}`}>
                    <input 
                      type="checkbox" 
                      className="w-4 h-4 text-emerald-500 rounded border-slate-350 focus:ring-emerald-500 mt-0.5"
                      checked={sendWhatsApp}
                      onChange={(e) => setSendWhatsApp(e.target.checked)}
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-1.5 font-bold text-xs text-slate-800 dark:text-slate-200">
                        <Smartphone className="w-4 h-4 text-emerald-500" /> WhatsApp
                      </div>
                      {sendWhatsApp ? (
                        <div className="mt-2 space-y-2">
                          <div className="flex flex-wrap gap-1">
                            {whatsappNumbers.map(phone => (
                              <span key={phone} className="flex items-center gap-1 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 px-2 py-0.5 rounded text-[10px] font-bold">
                                {phone}
                                <button type="button" onClick={() => handleRemoveWhatsapp(phone)} className="hover:text-emerald-900"><X className="w-3 h-3" /></button>
                              </span>
                            ))}
                          </div>
                          <div className="relative">
                            <input
                              type="text"
                              className="w-full bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-850 rounded p-1.5 pr-8 text-xs focus:outline-none focus:border-emerald-500"
                              value={whatsappInput}
                              onChange={(e) => { setWhatsappInput(e.target.value); }}
                              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddWhatsapp(); } }}
                              placeholder="Adicionar telefone..."
                            />
                            <button type="button" onClick={() => handleAddWhatsapp()} className="absolute right-2 top-2 text-emerald-500"><Plus className="w-4 h-4" /></button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-[10px] text-slate-500 mt-1">Destinatário: {whatsappNumbers.join(', ') || 'Nenhum'}</p>
                      )}
                    </div>
                  </label>

                  {/* Email selection */}
                  <label className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${sendEmail ? 'border-blue-500 bg-blue-500/5' : 'border-slate-200 dark:border-slate-800 hover:border-blue-500/20 bg-slate-950/20'}`}>
                    <input 
                      type="checkbox" 
                      className="w-4 h-4 text-blue-500 rounded border-slate-350 focus:ring-blue-500 mt-0.5"
                      checked={sendEmail}
                      onChange={(e) => setSendEmail(e.target.checked)}
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-1.5 font-bold text-xs text-slate-800 dark:text-slate-200">
                        <Mail className="w-4 h-4 text-blue-500" /> E-mail
                      </div>
                      {sendEmail ? (
                        <div className="mt-2 space-y-2">
                          <div className="flex flex-wrap gap-1">
                            {emailAddresses.map(email => (
                              <span key={email} className="flex items-center gap-1 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded text-[10px] font-bold">
                                {email}
                                <button type="button" onClick={() => handleRemoveEmail(email)} className="hover:text-blue-900"><X className="w-3 h-3" /></button>
                              </span>
                            ))}
                          </div>
                          <input
                            type="email"
                            className="w-full bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-850 rounded p-1.5 text-xs focus:outline-none focus:border-blue-500"
                            value={emailInput}
                            onChange={(e) => setEmailInput(e.target.value)}
                            onKeyDown={handleAddEmail}
                            placeholder="Adicionar e-mail..."
                          />
                        </div>
                      ) : (
                        <p className="text-[10px] text-slate-500 mt-1">Destinatário: {emailAddresses.join(', ') || 'Nenhum'}</p>
                      )}
                    </div>
                  </label>
                </div>
              </div>

              {/* Template & Authority configuration */}
              <div className="space-y-4 bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200 dark:border-slate-800">
                <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200 font-sans">2. Selecione o Template e Autoridades</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 font-mono">Modelo de Ofício</label>
                    <select
                      className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl py-2 px-3 text-xs text-slate-700 dark:text-slate-100 focus:outline-none"
                      value={selectedTemplateCode}
                      onChange={(e) => setSelectedTemplateCode(e.target.value)}
                    >
                      <option value="">Ofício Padrão (Livre)</option>
                      {templates.map(t => {
                        const isComingSoon = t.status === 'COMING_SOON';
                        return (
                          <option key={t.id} value={t.code} disabled={isComingSoon}>
                            {t.name} {isComingSoon ? '(Em breve)' : ''}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 font-mono">Signatário do Documento</label>
                    <select
                      className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl py-2 px-3 text-xs text-slate-700 dark:text-slate-100 focus:outline-none"
                      value={selectedAuthorityId}
                      onChange={(e) => setSelectedAuthorityId(e.target.value)}
                    >
                      <option value="">Assinatura Padrão (Dani Alonso)</option>
                      {authorities.map(auth => (
                        <option key={auth.id} value={auth.id}>
                          {auth.name} ({auth.role})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Templates COMING_SOON visual list */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-2">
                  {templates.map(t => {
                    const isComingSoon = t.status === 'COMING_SOON';
                    const isSelected = selectedTemplateCode === t.code;
                    return (
                      <div 
                        key={t.id}
                        onClick={() => !isComingSoon && setSelectedTemplateCode(t.code)}
                        className={`p-3 rounded-xl border text-left transition-all ${
                          isComingSoon 
                            ? 'opacity-50 cursor-not-allowed border-slate-250 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950/20' 
                            : isSelected
                              ? 'border-emerald-500 bg-emerald-500/5'
                              : 'border-slate-200 dark:border-slate-800 bg-slate-950/40 hover:border-slate-700 cursor-pointer'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[9px] font-bold font-mono text-slate-500">{t.institution}</span>
                          {isComingSoon && (
                            <span className="px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-500 text-[8px] font-bold font-sans">
                              Em breve
                            </span>
                          )}
                        </div>
                        <h4 className="text-xs font-bold text-slate-700 dark:text-slate-200 leading-tight mb-1">{t.name}</h4>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Dynamic input variables */}
              {activeTemplate && activeTemplate.variables && activeTemplate.variables.length > 0 && (
                <div className="space-y-4 bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200 dark:border-slate-800">
                  <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200 font-sans">3. Preencha as Variáveis do Documento</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {activeTemplate.variables.map((v: any) => (
                      <div key={v.key} className="space-y-1">
                        <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 font-mono">
                          {v.label} {v.required && <span className="text-red-500">*</span>}
                        </label>
                        {v.key.toLowerCase().replace(/[_\s-]/g, '') === 'bemsolicitado' ? (
                          <div className="space-y-1">
                            <select
                              onChange={(e) => {
                                  if (e.target.value) {
                                    setDynamicVars(prev => ({ ...prev, [v.key]: e.target.value }));
                                  }
                                }}
                                className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-250 dark:border-slate-850 rounded-lg py-1.5 px-3 text-xs focus:outline-none focus:border-emerald-500 text-white"
                              >
                                <option value="">Buscar no CRM...</option>
                                {resourceObjects.map(obj => (
                                  <option key={obj.id} value={obj.name}>
                                    [{obj.themeArea}] {obj.name}
                                  </option>
                                ))}
                                <option value={demand.title}>{demand.title} (Título)</option>
                              </select>
                              <input
                                type="text"
                                required={v.required}
                                value={dynamicVars[v.key] || ''}
                                onChange={(e) => setDynamicVars(prev => ({ ...prev, [v.key]: e.target.value }))}
                                className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-250 dark:border-slate-850 rounded-lg py-1.5 px-3 text-xs focus:outline-none focus:border-emerald-500 text-white"
                                placeholder="Ou insira manualmente..."
                              />
                            </div>
                          ) : (
                            <input
                              type="text"
                              required={v.required}
                              value={dynamicVars[v.key] || ''}
                              onChange={(e) => setDynamicVars(prev => ({ ...prev, [v.key]: e.target.value }))}
                              className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-250 dark:border-slate-850 rounded-lg py-1.5 px-3 text-xs focus:outline-none focus:border-emerald-500 text-white"
                              placeholder={`Inserir ${v.label.toLowerCase()}...`}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Variáveis para o Ofício Padrão (Livre) */}
                {!selectedTemplateCode && (
                  <div className="space-y-4 bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200 dark:border-slate-800">
                    <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200 font-sans">3. Preencha as Variáveis do Ofício</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      
                      {/* Campo Vereador */}
                      <div className="space-y-1 text-left">
                        <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 font-mono">
                          Vereador Solicitante
                        </label>
                        <input
                          type="text"
                          value={customVereador}
                          onChange={(e) => setCustomVereador(e.target.value)}
                          className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-250 dark:border-slate-850 rounded-lg py-1.5 px-3 text-xs focus:outline-none focus:border-emerald-500 text-white"
                          placeholder="Digite o nome do vereador (ex: Ricardo Rio)..."
                        />
                      </div>

                      {/* Campo Objeto/Destinação */}
                      <div className="space-y-1 text-left">
                        <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 font-mono">
                          Objeto / Finalidade da Emenda
                        </label>
                        <input
                          type="text"
                          value={customObjeto}
                          onChange={(e) => setCustomObjeto(e.target.value)}
                          className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-250 dark:border-slate-850 rounded-lg py-1.5 px-3 text-xs focus:outline-none focus:border-emerald-500 text-white"
                          placeholder="Ex: Custeio da Saúde, Reparos de Vicinais..."
                        />
                      </div>

                    </div>
                  </div>
                )}
            </div>
          )}

          {/* STEP 2: Inline Editor in A4 simulation */}
          {step === 2 && (
            <div className="space-y-4 flex flex-col h-full items-center">
              
              {/* Toolbar */}
              <div className="w-full max-w-5xl bg-white dark:bg-slate-900 p-3 rounded-xl border border-slate-200 dark:border-slate-800 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-300 select-none">
                <div className="flex items-center gap-3">
                  {/* Autosave Switch */}
                  <label className="flex items-center gap-1.5 cursor-pointer font-medium text-slate-400">
                    <input 
                      type="checkbox" 
                      checked={autoSaveActive}
                      onChange={e => setAutoSaveActive(e.target.checked)}
                      className="rounded bg-slate-950 border-slate-800 text-blue-600 focus:ring-0"
                    />
                    AutoSave {autoSaveActive ? '(Ativo)' : '(Inativo)'}
                  </label>
                  {lastSaved ? (
                    <span className="text-slate-500 text-[10px]">
                      Salvo às {lastSaved}
                    </span>
                  ) : hasUnsavedChanges ? (
                    <span className="text-amber-500 text-[10px]">Alterações pendentes...</span>
                  ) : (
                    <span className="text-emerald-500 text-[10px]">Salvo</span>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {/* Zoom controls */}
                  <div className="flex items-center bg-slate-950/60 rounded-lg border border-slate-800 p-0.5">
                    <button 
                      onClick={() => setZoom(prev => Math.max(prev - 10, 80))}
                      className="p-1 hover:text-blue-400 transition"
                    >
                      <ZoomOut className="w-3.5 h-3.5" />
                    </button>
                    <span className="px-2 font-mono text-[10px]">{zoom}%</span>
                    <button 
                      onClick={() => setZoom(prev => Math.min(prev + 10, 120))}
                      className="p-1 hover:text-blue-400 transition"
                    >
                      <ZoomIn className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  {/* Manual Save */}
                  <button 
                    onClick={handleManualSave} 
                    className="p-1.5 border border-slate-700 hover:bg-slate-800 hover:text-white rounded-lg transition"
                    title="Salvar Rascunho"
                  >
                    <Save className="w-4 h-4 text-slate-400 hover:text-white" />
                  </button>

                  {/* Revert Changes */}
                  <button 
                    onClick={handleRegenerar} 
                    className="p-1.5 border border-slate-700 hover:bg-slate-800 hover:text-white rounded-lg transition"
                    title="Regenerar Template Original"
                  >
                    <RotateCcw className="w-4 h-4 text-slate-400 hover:text-white" />
                  </button>

                  {/* Fullscreen toggle */}
                  <button 
                    onClick={() => setIsFullscreen(prev => !prev)}
                    className="p-1.5 border border-slate-700 hover:bg-slate-800 hover:text-white rounded-lg transition"
                  >
                    {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* A4 Document Visual Sheet */}
              <div className="w-full flex-1 overflow-auto flex justify-center py-4 bg-slate-200 dark:bg-slate-950/60 rounded-2xl border border-slate-350 dark:border-slate-850 min-h-[500px]">
                <div 
                  className="bg-white text-slate-950 shadow-2xl p-[25mm] font-serif text-justify flex flex-col transition-transform duration-200"
                  style={{
                    width: '210mm',
                    minHeight: '297mm',
                    transform: `scale(${zoom / 100})`,
                    transformOrigin: 'top center',
                    boxShadow: '0 25px 50px -12px rgb(0 0 0 / 0.25)',
                    border: '1px solid #d1d5db'
                  }}
                >
                  {/* Protected Header Section */}
                  <div contentEditable={false} className="select-none pb-2 mb-6 text-center">
                    <div className="flex justify-center gap-12 items-center mb-4">
                      <img src="/logo-camara.png" className="max-h-28 object-contain" />
                      <img src="/logo-alesp.png" className="max-h-28 object-contain" />
                    </div>
                  </div>

                  {/* Body Editable Section */}
                  <div 
                    id="body-editor"
                    contentEditable={true}
                    suppressContentEditableWarning={true}
                    onInput={e => {
                      setEditedHtml(e.currentTarget.innerHTML);
                      setHasUnsavedChanges(true);
                    }}
                    className="outline-none min-h-[400px] leading-relaxed text-[15px]"
                    dangerouslySetInnerHTML={{ __html: editedHtml }}
                  />

                  {/* Protected Signature Block */}
                  <div contentEditable={false} className="select-none mt-12 text-center flex flex-col items-center">
                    {activeAuthority ? (
                      <div className="flex flex-col items-center">
                        {activeAuthority.signatureImageUrl && (
                          <img src={activeAuthority.signatureImageUrl} className="max-h-28 object-contain mb-2" />
                        )}
                      </div>
                    ) : (
                      <div className="flex justify-center gap-16 w-full text-xs">
                        <div className="text-center flex flex-col items-center">
                          <img src="/assinatura-dani.png" className="max-h-24 object-contain mb-1" />
                        </div>
                        <div className="text-center flex flex-col items-center">
                          <img src="/assinatura-capitao.png" className="max-h-24 object-contain mb-1" />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* STEP 3: Preview PDF before definitive send */}
          {step === 3 && previewPdfUrl && (
            <div className="space-y-4 max-w-4xl mx-auto h-full flex flex-col">
              <div className="bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800 flex items-center justify-between text-xs text-slate-400">
                <span className="font-medium text-slate-300">Ofício Gerado com Sucesso: Visualização em PDF</span>
                <span className="text-amber-500 font-bold">Por favor, revise o layout final antes do envio.</span>
              </div>
              <div className="flex-1 min-h-[500px] border border-slate-800 rounded-xl overflow-hidden bg-slate-950">
                <iframe 
                  src={previewPdfUrl} 
                  className="w-full h-full border-none"
                  title="PDF Document Preview"
                />
              </div>
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-800/60 bg-slate-50 dark:bg-slate-955 flex items-center justify-between">
          <div>
            {step > 1 && (
              <button
                type="button"
                onClick={() => setStep(prev => prev - 1)}
                className="flex items-center gap-1.5 px-4 py-2 border border-slate-700 hover:bg-slate-800 text-slate-300 hover:text-white rounded-lg text-xs font-semibold transition"
              >
                <ArrowLeft className="w-4 h-4" /> Voltar
              </button>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 hover:bg-slate-200 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-200 rounded-lg text-xs font-semibold transition"
              disabled={isSubmitting}
            >
              Cancelar
            </button>

            {step === 1 && (
              <button
                type="button"
                onClick={handleNextStepToEditor}
                className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 text-white px-5 py-2.5 rounded-xl text-xs font-bold transition shadow-lg"
              >
                Revisar Ofício <ArrowRight className="w-4 h-4" />
              </button>
            )}

            {step === 2 && (
              <button
                type="button"
                onClick={handlePreviewPDF}
                className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-500 text-white px-5 py-2.5 rounded-xl text-xs font-bold transition shadow-lg"
              >
                Visualizar PDF <Eye className="w-4 h-4" />
              </button>
            )}

            {step === 3 && (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 text-white px-6 py-2.5 rounded-xl text-xs font-bold transition shadow-lg"
              >
                {isSubmitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    Aprovar e Enviar <Send className="w-4 h-4" />
                  </>
                )}
              </button>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};
